import { useRouterState } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { QuickCaptureDialog } from '../../features/capture/QuickCaptureDialog';
import { useGoScreen, useOpenRecord } from '../../lib/navigation';
import { Toaster } from '../ui/Toaster';
import { ActivityPanel } from './ActivityPanel';
import { CaptureProvider, useCapture } from './CaptureContext';
import { ConfirmProvider } from './ConfirmContext';
import { CommandPalette } from './CommandPalette';
import type { PaletteTarget } from './CommandPalette';
import type { ActivityFocus } from './queries';
import { ShellContext } from './ShellContext';
import type { ShellUi } from './ShellContext';
import { Sidebar } from './Sidebar';
import { useGlobalShortcuts } from './shortcuts';
import { useSidebarCollapsed } from './useSidebarCollapsed';

interface AppShellProps {
  readonly children: ReactNode;
}

/** App shell (design Main.dc.html): sidebar, a full-height scrolling main (no header, V120) and the shell overlays. */
export function AppShell({ children }: AppShellProps) {
  return (
    <ConfirmProvider>
      <CaptureProvider>
        <ShellFrame>{children}</ShellFrame>
      </CaptureProvider>
    </ConfirmProvider>
  );
}

interface ActivityState {
  readonly open: boolean;
  readonly focus: ActivityFocus | null;
}

function ShellFrame({ children }: AppShellProps) {
  const mainRef = useRef<HTMLElement>(null);
  const [collapsed, toggleSidebar] = useSidebarCollapsed();
  const [activity, setActivity] = useState<ActivityState>({ open: false, focus: null });
  const [paletteOpen, setPaletteOpen] = useState(false);
  const capture = useCapture();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const openRecord = useOpenRecord();
  const goScreen = useGoScreen();

  useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 });
  }, [pathname]);

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  const openActivity = useCallback(
    (focus?: ActivityFocus) => setActivity({ open: true, focus: focus ?? null }),
    [],
  );
  const toggleActivity = useCallback(() => setActivity((a) => ({ open: !a.open, focus: null })), []);
  const onPick = (target: PaletteTarget) =>
    target.type === 'screen' ? goScreen(target.id) : openRecord(target.type, target.id);

  useGlobalShortcuts({ onPalette: openPalette, onCapture: () => capture.open() });
  const shell = useMemo<ShellUi>(() => ({ openPalette, openActivity, mainRef }), [openPalette, openActivity]);

  return (
    <ShellContext.Provider value={shell}>
      <div className="flex h-screen overflow-hidden bg-bg-1 font-sans text-[13px] leading-[1.4] text-fg-1">
        <Sidebar collapsed={collapsed} onToggle={toggleSidebar} onToggleActivity={toggleActivity} />
        <main ref={mainRef} className="relative min-h-0 min-w-0 flex-1 overflow-auto [overflow-anchor:none]">
          {children}
        </main>
      </div>
      {capture.state.preset && (
        <QuickCaptureDialog preset={capture.state.preset} seq={capture.state.seq} onClose={capture.close} />
      )}
      {paletteOpen && <CommandPalette onClose={() => setPaletteOpen(false)} onPick={onPick} />}
      {activity.open && (
        <ActivityPanel focus={activity.focus} onClose={() => setActivity({ open: false, focus: null })} />
      )}
      <Toaster />
    </ShellContext.Provider>
  );
}
